import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLogo } from '../../context/LogoContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Zap, Mail, Lock, ArrowRight } from 'lucide-react';
import TurnstileWidget, { isTurnstileEnabled } from '../../components/auth/TurnstileWidget';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { logoUrl: globalLogo, hubName: globalHubName } = useLogo();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const [branding, setBranding] = useState({ hub_name: 'SpillConsult Hub', hub_logo: null });

  useEffect(() => {
    fetch(`${BACKEND}/api/hub/public-branding`)
      .then(r => r.json())
      .then(data => setBranding(data))
      .catch(() => {});
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/hub');
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isTurnstileEnabled && !turnstileToken) {
      toast.error('Please complete the verification challenge');
      return;
    }
    setLoading(true);
    try {
      await login(email, password, turnstileToken);
      toast.success('Welcome back!');
      navigate('/hub');
    } catch (error) {
      toast.error(error.message || 'Login failed');
      // V14.12 — clear stale token on every failure
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#06070a] relative overflow-hidden">
      {/* Grid background */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-500/[0.06] rounded-full blur-[100px]" />
      <div className="absolute bottom-1/4 left-1/3 w-[400px] h-[200px] bg-purple-500/[0.04] rounded-full blur-[80px]" />

      <div className="relative z-10 w-full max-w-md px-4">
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm overflow-hidden">
          <div className="p-8">
            {/* Logo */}
            <div className="text-center mb-8">
              <div className="mx-auto w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/20 overflow-hidden">
                {(globalLogo || branding.hub_logo) ? (
                  <img src={globalLogo || `${BACKEND}${branding.hub_logo}`} alt={globalHubName || branding.hub_name} className="w-full h-full object-contain p-1" />
                ) : (
                  <Zap className="w-7 h-7 text-white" />
                )}
              </div>
              <h1 className="text-2xl font-bold text-white tracking-tight">{globalHubName || branding.hub_name}</h1>
              <p className="text-sm text-white/30 mt-1">Exercise & Media Management Platform</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-white/40">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
                  <Input
                    type="email"
                    placeholder="name@example.com"
                    className="pl-10 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus:border-blue-500/50 focus:ring-blue-500/20 h-11"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-white/40">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus:border-blue-500/50 focus:ring-blue-500/20 h-11"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="password-input"
                  />
                </div>
                <div className="text-right">
                  <Link to="/forgot-password" className="text-xs text-blue-400 hover:text-blue-300 transition-colors" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
              </div>

              <div className="flex justify-center">
                <TurnstileWidget ref={turnstileRef} action="login" onVerify={setTurnstileToken} theme="dark" />
              </div>

              <Button
                type="submit"
                disabled={loading || (isTurnstileEnabled && !turnstileToken)}
                className="w-full h-11 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-medium shadow-lg shadow-blue-500/20 transition-all"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Sign In <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </form>
          </div>

          <div className="px-8 py-4 border-t border-white/[0.04] text-center">
            <p className="text-[11px] text-white/20">
              Protected system. Authorized users only.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
